export type AgreementFormValues = Record<string, unknown>;

export type AgreementFieldBlockType =
  | "shortText"
  | "longText"
  | "email"
  | "date"
  | "time"
  | "multipleChoice"
  | "checkboxes"
  | "dropdown"
  | "linearScale"
  | "rating"
  | "gridSingle"
  | "gridMulti";

export type AgreementContentBlockType = "heading" | "paragraph" | "multimodalText";
export type AgreementSpecialBlockType = "signature";

export type AgreementBlockType =
  | AgreementContentBlockType
  | AgreementFieldBlockType
  | AgreementSpecialBlockType;

export interface AgreementFormAttachment {
  alt: string;
  caption?: string;
  id: string;
  kind: "image";
  url: string;
}

export interface AgreementFormOption {
  description?: string;
  goToSectionId?: string;
  id: string;
  label: string;
  value: string;
}

export interface AgreementFormValidation {
  errorText?: string;
  max?: number;
  maxLength?: number;
  maxSelected?: number;
  min?: number;
  minLength?: number;
  minSelected?: number;
  pattern?: string;
  requiredRows?: boolean;
}

interface AgreementBaseBlock {
  description?: string;
  id: string;
  type: AgreementBlockType;
}

export interface AgreementHeadingBlock extends AgreementBaseBlock {
  text: string;
  type: "heading";
}

export interface AgreementParagraphBlock extends AgreementBaseBlock {
  text: string;
  type: "paragraph";
}

export interface AgreementMultimodalTextBlock extends AgreementBaseBlock {
  attachments: AgreementFormAttachment[];
  richTextHtml: string;
  type: "multimodalText";
}

interface AgreementBaseFieldBlock extends AgreementBaseBlock {
  fieldKey: string;
  helperText?: string;
  label: string;
  required?: boolean;
  validation?: AgreementFormValidation;
}

export interface AgreementShortTextBlock extends AgreementBaseFieldBlock {
  defaultValue?: string;
  placeholder?: string;
  type: "shortText";
}

export interface AgreementLongTextBlock extends AgreementBaseFieldBlock {
  defaultValue?: string;
  placeholder?: string;
  rows?: number;
  type: "longText";
}

export interface AgreementEmailBlock extends AgreementBaseFieldBlock {
  defaultValue?: string;
  placeholder?: string;
  type: "email";
}

export interface AgreementDateBlock extends AgreementBaseFieldBlock {
  defaultValue?: string;
  type: "date";
}

export interface AgreementTimeBlock extends AgreementBaseFieldBlock {
  defaultValue?: string;
  type: "time";
}

export interface AgreementMultipleChoiceBlock extends AgreementBaseFieldBlock {
  defaultValue?: string;
  options: AgreementFormOption[];
  type: "multipleChoice";
}

export interface AgreementCheckboxesBlock extends AgreementBaseFieldBlock {
  defaultValue?: string[];
  options: AgreementFormOption[];
  type: "checkboxes";
}

export interface AgreementDropdownBlock extends AgreementBaseFieldBlock {
  defaultValue?: string;
  options: AgreementFormOption[];
  type: "dropdown";
}

export interface AgreementLinearScaleBlock extends AgreementBaseFieldBlock {
  defaultValue?: number;
  max: number;
  maxLabel?: string;
  min: number;
  minLabel?: string;
  type: "linearScale";
}

export interface AgreementRatingBlock extends AgreementBaseFieldBlock {
  defaultValue?: number;
  max: number;
  icon?: "heart" | "star" | "thumb";
  type: "rating";
}

export interface AgreementGridSingleBlock extends AgreementBaseFieldBlock {
  columns: Array<{ id: string; label: string }>;
  rows: Array<{ id: string; label: string }>;
  type: "gridSingle";
}

export interface AgreementGridMultiBlock extends AgreementBaseFieldBlock {
  columns: Array<{ id: string; label: string }>;
  rows: Array<{ id: string; label: string }>;
  type: "gridMulti";
}

export interface AgreementSignatureBlock extends AgreementBaseBlock {
  acceptedConfidentialityLabel: string;
  acceptedElectronicSignatureLabel: string;
  acceptedTermsLabel: string;
  helperText?: string;
  locationLabel: string;
  nameLabel: string;
  required?: boolean;
  signedAtLabel: string;
  title: string;
  type: "signature";
}

export type AgreementFieldBlock =
  | AgreementShortTextBlock
  | AgreementLongTextBlock
  | AgreementEmailBlock
  | AgreementDateBlock
  | AgreementTimeBlock
  | AgreementMultipleChoiceBlock
  | AgreementCheckboxesBlock
  | AgreementDropdownBlock
  | AgreementLinearScaleBlock
  | AgreementRatingBlock
  | AgreementGridSingleBlock
  | AgreementGridMultiBlock;

export type AgreementFormBlock =
  | AgreementHeadingBlock
  | AgreementParagraphBlock
  | AgreementMultimodalTextBlock
  | AgreementFieldBlock
  | AgreementSignatureBlock;

export interface AgreementFormSection {
  blocks: AgreementFormBlock[];
  description?: string;
  id: string;
  title: string;
}

export interface AgreementFormSettings {
  allowEditAfterSubmit: boolean;
  close: {
    enabled: boolean;
    message: string;
    responseLimit?: number;
  };
  collectEmailMode: "input" | "none" | "verified";
  confirmation: {
    message: string;
    mode: "message";
  };
  limitToOneSubmissionPerUser: boolean;
  requiresLogin: boolean;
}

export interface AgreementFormLogicRule {
  action: {
    sectionId: string;
    type: "goToSection";
  };
  when: {
    op: "equals";
    questionId: string;
    value: string;
  };
}

export interface AgreementFormSchema {
  description?: string;
  logic: AgreementFormLogicRule[];
  schemaVersion: 1;
  sections: AgreementFormSection[];
  settings: AgreementFormSettings;
  title: string;
}

export const CORE_FORM_FIELD_KEYS = {
  acceptedConfidentiality: "acceptedConfidentiality",
  acceptedElectronicSignature: "acceptedElectronicSignature",
  acceptedTerms: "acceptedTerms",
  agreementDate: "agreementDate",
  cityState: "cityState",
  companyAddress: "companyAddress",
  confidentialityPeriod: "confidentialityPeriod",
  course: "course",
  endDate: "endDate",
  internAddress: "internAddress",
  internshipType: "internshipType",
  jurisdictionCity: "jurisdictionCity",
  notes: "notes",
  signatureDataUrl: "signatureDataUrl",
  signatureLocation: "signatureLocation",
  signatureName: "signatureName",
  signedAt: "signedAt",
  startDate: "startDate",
  stipendAmount: "stipendAmount",
  workingHours: "workingHours",
  yearOfStudy: "yearOfStudy"
} as const;

const DEFAULT_AGREEMENT_LOCATION = "Mysore, Karnataka";
const DEFAULT_INTERNSHIP_TYPE = "paid";
const DEFAULT_SIGNATURE_LOCATION = "Mysore";

const REMOVED_FIELD_KEYS = new Set<string>([
  CORE_FORM_FIELD_KEYS.companyAddress,
  CORE_FORM_FIELD_KEYS.course,
  CORE_FORM_FIELD_KEYS.notes,
  CORE_FORM_FIELD_KEYS.yearOfStudy
]);

const DEFAULT_SETTINGS: AgreementFormSettings = {
  requiresLogin: true,
  limitToOneSubmissionPerUser: true,
  allowEditAfterSubmit: false,
  collectEmailMode: "verified",
  confirmation: {
    mode: "message",
    message: "Thanks. Your internship agreement has been submitted successfully."
  },
  close: {
    enabled: false,
    message: "This agreement form is currently closed."
  }
};

function createId(prefix: string) {
  return `${prefix}-${Math.random().toString(36).slice(2, 9)}`;
}

function slugifyFieldKey(value: string) {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "") || `field_${Math.random().toString(36).slice(2, 6)}`;
}

export function buildFormLogic(sections: AgreementFormSection[]): AgreementFormLogicRule[] {
  const rules: AgreementFormLogicRule[] = [];

  for (const section of sections) {
    for (const block of section.blocks) {
      if (block.type !== "multipleChoice" && block.type !== "dropdown") {
        continue;
      }

      for (const option of block.options) {
        if (!option.goToSectionId) {
          continue;
        }

        rules.push({
          when: {
            questionId: block.fieldKey,
            op: "equals",
            value: option.value
          },
          action: {
            type: "goToSection",
            sectionId: option.goToSectionId
          }
        });
      }
    }
  }

  return rules;
}

export function createDefaultAgreementFormSchema(): AgreementFormSchema {
  const sections: AgreementFormSection[] = [
    {
      id: "agreement-overview",
      title: "Agreement Overview",
      description: "Present the agreement context before collecting structured data.",
      blocks: [
        {
          id: "agreement-heading",
          type: "heading",
          text: "INTERNSHIP AGREEMENT (INDIA)"
        },
        {
          id: "agreement-intro",
          type: "multimodalText",
          description: "This block is intended for document-style clauses and optional images.",
          richTextHtml:
            "<p>Review the internship agreement carefully, complete the required details, and confirm the declarations before submission.</p><p>The terms and data protection notice remain part of the final signed record.</p>",
          attachments: []
        }
      ]
    },
    {
      id: "intern-details",
      title: "Intern Details",
      description: "Capture the date and address details that personalise the agreement.",
      blocks: [
        {
          id: "agreement-date",
          type: "date",
          fieldKey: CORE_FORM_FIELD_KEYS.agreementDate,
          label: "Agreement date",
          required: true
        },
        {
          id: "intern-address",
          type: "longText",
          fieldKey: CORE_FORM_FIELD_KEYS.internAddress,
          label: "Your address",
          required: true,
          rows: 4,
          placeholder: "Enter your current residential address."
        }
      ]
    },
    {
      id: "internship-terms",
      title: "Internship Terms",
      description: "Capture the internship dates and the fixed commercial details for this agreement.",
      blocks: [
        {
          id: "start-date",
          type: "date",
          fieldKey: CORE_FORM_FIELD_KEYS.startDate,
          label: "Internship start date",
          required: true
        },
        {
          id: "end-date",
          type: "date",
          fieldKey: CORE_FORM_FIELD_KEYS.endDate,
          label: "Internship end date",
          required: true
        },
        {
          id: "working-hours",
          type: "shortText",
          fieldKey: CORE_FORM_FIELD_KEYS.workingHours,
          label: "Working hours",
          required: true,
          helperText: "Fixed for this internship batch."
        },
        {
          id: "stipend-amount",
          type: "shortText",
          fieldKey: CORE_FORM_FIELD_KEYS.stipendAmount,
          label: "Stipend",
          required: true,
          placeholder: "15000",
          helperText: "Enter digits only, up to 5 numbers.",
          validation: {
            maxLength: 5,
            pattern: "^\\d{1,5}$",
            errorText: "Enter a stipend amount using up to 5 digits."
          }
        },
        {
          id: "confidentiality-period",
          type: "shortText",
          fieldKey: CORE_FORM_FIELD_KEYS.confidentialityPeriod,
          label: "Confidentiality survival period",
          required: true
        }
      ]
    },
    {
      id: "signature-consent",
      title: "Signature and Consent",
      description: "Capture the name, signature, date, and final declarations.",
      blocks: [
        {
          id: "signature-block",
          type: "signature",
          title: "Intern signature",
          required: true,
          nameLabel: "Signature name",
          locationLabel: "Signing location",
          signedAtLabel: "Signing date",
          acceptedTermsLabel: "I have read and accept the internship agreement terms.",
          acceptedConfidentialityLabel:
            "I acknowledge the confidentiality and data-handling obligations.",
          acceptedElectronicSignatureLabel:
            "I confirm that this electronic signature represents my acceptance."
        }
      ]
    }
  ];

  return {
    schemaVersion: 1,
    title: "Internship Agreement (India)",
    description: "Schema-driven agreement form with multimodal text and signature capture.",
    settings: DEFAULT_SETTINGS,
    sections,
    logic: buildFormLogic(sections)
  };
}

export function createBlankAgreementFormSchema(): AgreementFormSchema {
  return {
    schemaVersion: 1,
    title: "",
    description: undefined,
    settings: DEFAULT_SETTINGS,
    sections: [],
    logic: []
  };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function coerceString(value: unknown, fallback = "") {
  return typeof value === "string" ? value : fallback;
}

function coerceBoolean(value: unknown, fallback = false) {
  return typeof value === "boolean" ? value : fallback;
}

function coerceNumber(value: unknown, fallback: number) {
  return typeof value === "number" && Number.isFinite(value) ? value : fallback;
}

function normalizeOption(value: unknown, index: number): AgreementFormOption {
  const record = isRecord(value) ? value : {};
  const label = coerceString(record.label, `Option ${index + 1}`);
  return {
    id: coerceString(record.id, createId("option")),
    label,
    value: coerceString(record.value, slugifyFieldKey(label)),
    description: coerceString(record.description) || undefined,
    goToSectionId: coerceString(record.goToSectionId) || undefined
  };
}

function normalizeAttachment(value: unknown, index: number): AgreementFormAttachment {
  const record = isRecord(value) ? value : {};
  return {
    id: coerceString(record.id, createId("asset")),
    kind: "image",
    url: coerceString(record.url),
    alt: coerceString(record.alt, `Attachment ${index + 1}`),
    caption: coerceString(record.caption) || undefined
  };
}

function normalizeValidation(value: unknown): AgreementFormValidation | undefined {
  if (!isRecord(value)) {
    return undefined;
  }

  return {
    minLength: typeof value.minLength === "number" ? value.minLength : undefined,
    maxLength: typeof value.maxLength === "number" ? value.maxLength : undefined,
    min: typeof value.min === "number" ? value.min : undefined,
    max: typeof value.max === "number" ? value.max : undefined,
    minSelected: typeof value.minSelected === "number" ? value.minSelected : undefined,
    maxSelected: typeof value.maxSelected === "number" ? value.maxSelected : undefined,
    pattern: coerceString(value.pattern) || undefined,
    errorText: coerceString(value.errorText) || undefined,
    requiredRows: typeof value.requiredRows === "boolean" ? value.requiredRows : undefined
  };
}

function normalizeRowOrColumn(
  value: unknown,
  prefix: "column" | "row",
  index: number
): { id: string; label: string } {
  const record = isRecord(value) ? value : {};
  return {
    id: coerceString(record.id, createId(prefix)),
    label: coerceString(record.label, `${prefix === "row" ? "Row" : "Column"} ${index + 1}`)
  };
}

function normalizeBlock(value: unknown, index: number): AgreementFormBlock {
  const record = isRecord(value) ? value : {};
  const type = coerceString(record.type, "shortText") as AgreementBlockType;
  const base = {
    id: coerceString(record.id, createId("block")),
    description: coerceString(record.description) || undefined
  };

  if (type === "heading") {
    return {
      ...base,
      type,
      text: coerceString(record.text, "Section heading")
    };
  }

  if (type === "paragraph") {
    return {
      ...base,
      type,
      text: coerceString(record.text, "Paragraph copy")
    };
  }

  if (type === "multimodalText") {
    return {
      ...base,
      type,
      richTextHtml: coerceString(record.richTextHtml, "<p>Describe this section.</p>"),
      attachments: Array.isArray(record.attachments)
        ? record.attachments.map(normalizeAttachment)
        : []
    };
  }

  if (type === "signature") {
    return {
      ...base,
      type,
      title: coerceString(record.title, "Signature"),
      helperText: coerceString(record.helperText) || undefined,
      required: coerceBoolean(record.required, true),
      nameLabel: coerceString(record.nameLabel, "Signature name"),
      locationLabel: coerceString(record.locationLabel, "Signing location"),
      signedAtLabel: coerceString(record.signedAtLabel, "Signing date"),
      acceptedTermsLabel: coerceString(
        record.acceptedTermsLabel,
        "I have read and accept the agreement terms."
      ),
      acceptedConfidentialityLabel: coerceString(
        record.acceptedConfidentialityLabel,
        "I acknowledge the confidentiality obligations."
      ),
      acceptedElectronicSignatureLabel: coerceString(
        record.acceptedElectronicSignatureLabel,
        "I confirm this electronic signature represents my acceptance."
      )
    };
  }

  const fieldBase = {
    ...base,
    type: ([
      "shortText",
      "longText",
      "email",
      "date",
      "time",
      "multipleChoice",
      "checkboxes",
      "dropdown",
      "linearScale",
      "rating",
      "gridSingle",
      "gridMulti"
    ].includes(type)
      ? type
      : "shortText") as AgreementFieldBlockType,
    fieldKey: coerceString(record.fieldKey, `field_${index + 1}`),
    label: coerceString(record.label, "Untitled field"),
    helperText: coerceString(record.helperText) || undefined,
    required: coerceBoolean(record.required, false),
    validation: normalizeValidation(record.validation)
  };

  switch (fieldBase.type) {
    case "shortText":
      return {
        ...fieldBase,
        type: "shortText",
        placeholder: coerceString(record.placeholder) || undefined,
        defaultValue: coerceString(record.defaultValue) || undefined
      };
    case "longText":
      return {
        ...fieldBase,
        type: "longText",
        placeholder: coerceString(record.placeholder) || undefined,
        defaultValue: coerceString(record.defaultValue) || undefined,
        rows: coerceNumber(record.rows, 4)
      };
    case "email":
      return {
        ...fieldBase,
        type: "email",
        placeholder: coerceString(record.placeholder) || undefined,
        defaultValue: coerceString(record.defaultValue) || undefined
      };
    case "date":
      return {
        ...fieldBase,
        type: "date",
        defaultValue: coerceString(record.defaultValue) || undefined
      };
    case "time":
      return {
        ...fieldBase,
        type: "time",
        defaultValue: coerceString(record.defaultValue) || undefined
      };
    case "multipleChoice":
      return {
        ...fieldBase,
        type: "multipleChoice",
        defaultValue: coerceString(record.defaultValue) || undefined,
        options: Array.isArray(record.options) ? record.options.map(normalizeOption) : []
      };
    case "checkboxes":
      return {
        ...fieldBase,
        type: "checkboxes",
        defaultValue: Array.isArray(record.defaultValue)
          ? record.defaultValue.filter((entry): entry is string => typeof entry === "string")
          : undefined,
        options: Array.isArray(record.options) ? record.options.map(normalizeOption) : []
      };
    case "dropdown":
      return {
        ...fieldBase,
        type: "dropdown",
        defaultValue: coerceString(record.defaultValue) || undefined,
        options: Array.isArray(record.options) ? record.options.map(normalizeOption) : []
      };
    case "linearScale":
      return {
        ...fieldBase,
        type: "linearScale",
        min: coerceNumber(record.min, 1),
        max: coerceNumber(record.max, 5),
        minLabel: coerceString(record.minLabel) || undefined,
        maxLabel: coerceString(record.maxLabel) || undefined,
        defaultValue: typeof record.defaultValue === "number" ? record.defaultValue : undefined
      };
    case "rating":
      return {
        ...fieldBase,
        type: "rating",
        max: coerceNumber(record.max, 5),
        icon:
          record.icon === "heart" || record.icon === "thumb" || record.icon === "star"
            ? record.icon
            : "star",
        defaultValue: typeof record.defaultValue === "number" ? record.defaultValue : undefined
      };
    case "gridSingle":
      return {
        ...fieldBase,
        type: "gridSingle",
        rows: Array.isArray(record.rows) ? record.rows.map((item, itemIndex) => normalizeRowOrColumn(item, "row", itemIndex)) : [],
        columns: Array.isArray(record.columns)
          ? record.columns.map((item, itemIndex) => normalizeRowOrColumn(item, "column", itemIndex))
          : []
      };
    case "gridMulti":
      return {
        ...fieldBase,
        type: "gridMulti",
        rows: Array.isArray(record.rows) ? record.rows.map((item, itemIndex) => normalizeRowOrColumn(item, "row", itemIndex)) : [],
        columns: Array.isArray(record.columns)
          ? record.columns.map((item, itemIndex) => normalizeRowOrColumn(item, "column", itemIndex))
          : []
      };
  }
}

function normalizeSection(value: unknown, index: number): AgreementFormSection {
  const record = isRecord(value) ? value : {};

  return {
    id: coerceString(record.id, `section_${index + 1}`),
    title: coerceString(record.title, `Section ${index + 1}`),
    description: coerceString(record.description) || undefined,
    blocks: Array.isArray(record.blocks)
      ? record.blocks
          .map(normalizeBlock)
          .filter((block) => !("fieldKey" in block && REMOVED_FIELD_KEYS.has(block.fieldKey)))
      : []
  };
}

function legacyFieldToBlock(value: unknown): AgreementFormBlock | null {
  if (!isRecord(value)) {
    return null;
  }

  const id = coerceString(value.id);
  const label = coerceString(value.label, id || "Untitled field");
  const required = coerceBoolean(value.required, false);

  if (!id || id === "acceptanceDeclarations") {
    return null;
  }

  const common = {
    id: createId(id),
    fieldKey: id,
    label,
    required
  };

  switch (value.type) {
    case "date":
      return {
        ...common,
        type: "date"
      };
    case "textarea":
      return {
        ...common,
        type: "longText",
        rows: 4
      };
    case "select":
      return {
        ...common,
        type: "dropdown",
        options: Array.isArray(value.options)
          ? value.options.map((entry, index) =>
              normalizeOption(
                isRecord(entry) ? entry : { id: `${id}_${index}`, label: String(entry), value: String(entry) },
                index
              )
            )
          : []
      };
    default:
      return {
        ...common,
        type: "shortText"
      };
  }
}

function normalizeLegacySchema(input: unknown): AgreementFormSchema {
  const base = createDefaultAgreementFormSchema();

  if (!Array.isArray(input)) {
    return base;
  }

  const blocks = input
    .map(legacyFieldToBlock)
    .filter(
      (block): block is AgreementFormBlock =>
        block !== null && (!("fieldKey" in block) || !REMOVED_FIELD_KEYS.has(block.fieldKey))
    );
  const firstSections = base.sections.slice(0, 1);
  const signatureSection = base.sections.at(-1);

  return {
    ...base,
    sections: [
      ...firstSections,
      {
        id: "legacy-fields",
        title: "Agreement Fields",
        description: "Migrated from the earlier flat agreement form schema.",
        blocks
      },
      ...(signatureSection ? [signatureSection] : [])
    ],
    logic: []
  };
}

export function normalizeAgreementFormSchema(input: unknown): AgreementFormSchema {
  if (Array.isArray(input)) {
    return normalizeLegacySchema(input);
  }

  if (!isRecord(input) || !Array.isArray(input.sections)) {
    return createDefaultAgreementFormSchema();
  }

  const settings = isRecord(input.settings) ? input.settings : {};
  const sections = input.sections.map(normalizeSection);

  return {
    schemaVersion: 1,
    title: coerceString(input.title, "Internship Agreement (India)"),
    description: coerceString(input.description) || undefined,
    settings: {
      requiresLogin: coerceBoolean(settings.requiresLogin, DEFAULT_SETTINGS.requiresLogin),
      limitToOneSubmissionPerUser: coerceBoolean(
        settings.limitToOneSubmissionPerUser,
        DEFAULT_SETTINGS.limitToOneSubmissionPerUser
      ),
      allowEditAfterSubmit: coerceBoolean(
        settings.allowEditAfterSubmit,
        DEFAULT_SETTINGS.allowEditAfterSubmit
      ),
      collectEmailMode:
        settings.collectEmailMode === "input" ||
        settings.collectEmailMode === "none" ||
        settings.collectEmailMode === "verified"
          ? settings.collectEmailMode
          : DEFAULT_SETTINGS.collectEmailMode,
      confirmation: {
        mode: "message",
        message: isRecord(settings.confirmation)
          ? coerceString(settings.confirmation.message, DEFAULT_SETTINGS.confirmation.message)
          : DEFAULT_SETTINGS.confirmation.message
      },
      close: {
        enabled: isRecord(settings.close)
          ? coerceBoolean(settings.close.enabled, DEFAULT_SETTINGS.close.enabled)
          : DEFAULT_SETTINGS.close.enabled,
        message: isRecord(settings.close)
          ? coerceString(settings.close.message, DEFAULT_SETTINGS.close.message)
          : DEFAULT_SETTINGS.close.message,
        responseLimit:
          isRecord(settings.close) && typeof settings.close.responseLimit === "number"
            ? settings.close.responseLimit
            : undefined
      }
    },
    sections,
    logic: buildFormLogic(sections)
  };
}

export function createEmptyBlock(type: AgreementBlockType): AgreementFormBlock {
  switch (type) {
    case "heading":
      return {
        id: createId("heading"),
        type,
        text: "Section heading"
      };
    case "paragraph":
      return {
        id: createId("paragraph"),
        type,
        text: "Explain what the responder needs to know in this section."
      };
    case "multimodalText":
      return {
        id: createId("multimodal"),
        type,
        richTextHtml: "<p>Paste the agreement clause, process guidance, or policy copy here.</p>",
        attachments: []
      };
    case "signature":
      return {
        id: createId("signature"),
        type,
        title: "Signature",
        helperText: "Capture the signature and required declarations here.",
        required: true,
        nameLabel: "Signature name",
        locationLabel: "Signing location",
        signedAtLabel: "Signing date",
        acceptedTermsLabel: "I have read and accept the agreement terms.",
        acceptedConfidentialityLabel: "I acknowledge the confidentiality obligations.",
        acceptedElectronicSignatureLabel:
          "I confirm this electronic signature represents my acceptance."
      };
    case "multipleChoice":
    case "checkboxes":
    case "dropdown":
      return {
        id: createId(type),
        type,
        fieldKey: slugifyFieldKey(`${type}_${Date.now()}`),
        label: "Untitled question",
        required: false,
        options: [
          {
            id: createId("option"),
            label: "Option 1",
            value: "option_1"
          },
          {
            id: createId("option"),
            label: "Option 2",
            value: "option_2"
          }
        ]
      } as AgreementMultipleChoiceBlock | AgreementCheckboxesBlock | AgreementDropdownBlock;
    case "linearScale":
      return {
        id: createId(type),
        type,
        fieldKey: slugifyFieldKey(`${type}_${Date.now()}`),
        label: "Untitled scale",
        min: 1,
        max: 5,
        minLabel: "Low",
        maxLabel: "High"
      };
    case "rating":
      return {
        id: createId(type),
        type,
        fieldKey: slugifyFieldKey(`${type}_${Date.now()}`),
        label: "Untitled rating",
        max: 5,
        icon: "star"
      };
    case "gridSingle":
    case "gridMulti":
      return {
        id: createId(type),
        type,
        fieldKey: slugifyFieldKey(`${type}_${Date.now()}`),
        label: "Untitled grid",
        rows: [
          {
            id: createId("row"),
            label: "Row 1"
          },
          {
            id: createId("row"),
            label: "Row 2"
          }
        ],
        columns: [
          {
            id: createId("column"),
            label: "Column 1"
          },
          {
            id: createId("column"),
            label: "Column 2"
          }
        ]
      } as AgreementGridSingleBlock | AgreementGridMultiBlock;
    default:
      return {
        id: createId(type),
        type,
        fieldKey: slugifyFieldKey(`${type}_${Date.now()}`),
        label: "Untitled field",
        required: false
      } as AgreementShortTextBlock | AgreementLongTextBlock | AgreementEmailBlock | AgreementDateBlock | AgreementTimeBlock;
  }
}

export function isFieldBlock(block: AgreementFormBlock): block is AgreementFieldBlock {
  return (
    block.type !== "heading" &&
    block.type !== "paragraph" &&
    block.type !== "multimodalText" &&
    block.type !== "signature"
  );
}

export function getStringValue(values: AgreementFormValues, key: string, fallback = "") {
  return typeof values[key] === "string" ? (values[key] as string) : fallback;
}

export function getBooleanValue(values: AgreementFormValues, key: string, fallback = false) {
  return typeof values[key] === "boolean" ? (values[key] as boolean) : fallback;
}

export function getNumberValue(values: AgreementFormValues, key: string, fallback = 0) {
  return typeof values[key] === "number" ? (values[key] as number) : fallback;
}

export function getStringArrayValue(values: AgreementFormValues, key: string) {
  const value = values[key];
  return Array.isArray(value) ? value.filter((entry): entry is string => typeof entry === "string") : [];
}

export function getTodayDateInputValue(date = new Date()) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export function addDaysToDateInputValue(value: string, days: number) {
  if (!value) {
    return value;
  }

  const [year, month, day] = value.split("-").map((segment) => Number(segment));

  if (!year || !month || !day) {
    return value;
  }

  const nextDate = new Date(year, month - 1, day);
  nextDate.setDate(nextDate.getDate() + days);
  return getTodayDateInputValue(nextDate);
}

export function getGridSingleValue(values: AgreementFormValues, key: string) {
  const value = values[key];
  if (!isRecord(value)) {
    return {} as Record<string, string>;
  }

  return Object.fromEntries(
    Object.entries(value).filter((entry): entry is [string, string] => typeof entry[1] === "string")
  );
}

export function getGridMultiValue(values: AgreementFormValues, key: string) {
  const value = values[key];

  if (!isRecord(value)) {
    return {} as Record<string, string[]>;
  }

  return Object.fromEntries(
    Object.entries(value).map(([rowId, rowValue]) => [
      rowId,
      Array.isArray(rowValue)
        ? rowValue.filter((entry): entry is string => typeof entry === "string")
        : []
    ])
  );
}

function validateTextLike(block: AgreementBaseFieldBlock, rawValue: unknown) {
  const value = typeof rawValue === "string" ? rawValue.trim() : "";

  if (block.required && value.length === 0) {
    return block.validation?.errorText ?? `${block.label} is required.`;
  }

  if (value.length === 0) {
    return null;
  }

  if (typeof block.validation?.minLength === "number" && value.length < block.validation.minLength) {
    return block.validation.errorText ?? `${block.label} is too short.`;
  }

  if (typeof block.validation?.maxLength === "number" && value.length > block.validation.maxLength) {
    return block.validation.errorText ?? `${block.label} is too long.`;
  }

  if (block.type === "email") {
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailPattern.test(value)) {
      return block.validation?.errorText ?? `Enter a valid email for ${block.label}.`;
    }
  }

  if (block.validation?.pattern) {
    try {
      const pattern = new RegExp(block.validation.pattern);
      if (!pattern.test(value)) {
        return block.validation.errorText ?? `${block.label} does not match the expected format.`;
      }
    } catch {
      return null;
    }
  }

  return null;
}

export function getBlockValidationError(
  block: AgreementFormBlock,
  values: AgreementFormValues
): string | null {
  if (block.type === "heading" || block.type === "paragraph" || block.type === "multimodalText") {
    return null;
  }

  if (block.type === "signature") {
    if (block.required && getStringValue(values, CORE_FORM_FIELD_KEYS.signatureDataUrl).length === 0) {
      return "A signature is required before you can continue.";
    }

    if (!getStringValue(values, CORE_FORM_FIELD_KEYS.signatureName).trim()) {
      return `${block.nameLabel} is required.`;
    }

    if (!getStringValue(values, CORE_FORM_FIELD_KEYS.signedAt).trim()) {
      return `${block.signedAtLabel} is required.`;
    }

    if (!getBooleanValue(values, CORE_FORM_FIELD_KEYS.acceptedTerms)) {
      return block.acceptedTermsLabel;
    }

    if (!getBooleanValue(values, CORE_FORM_FIELD_KEYS.acceptedConfidentiality)) {
      return block.acceptedConfidentialityLabel;
    }

    if (!getBooleanValue(values, CORE_FORM_FIELD_KEYS.acceptedElectronicSignature)) {
      return block.acceptedElectronicSignatureLabel;
    }

    return null;
  }

  if (
    block.type === "shortText" ||
    block.type === "longText" ||
    block.type === "email" ||
    block.type === "date" ||
    block.type === "time"
  ) {
    return validateTextLike(block, values[block.fieldKey]);
  }

  if (block.type === "multipleChoice" || block.type === "dropdown") {
    const value = getStringValue(values, block.fieldKey);
    if (block.required && !value) {
      return block.validation?.errorText ?? `${block.label} is required.`;
    }

    return null;
  }

  if (block.type === "checkboxes") {
    const value = getStringArrayValue(values, block.fieldKey);

    if (block.required && value.length === 0) {
      return block.validation?.errorText ?? `Select at least one option for ${block.label}.`;
    }

    if (typeof block.validation?.minSelected === "number" && value.length < block.validation.minSelected) {
      return block.validation.errorText ?? `Select more options for ${block.label}.`;
    }

    if (typeof block.validation?.maxSelected === "number" && value.length > block.validation.maxSelected) {
      return block.validation.errorText ?? `Select fewer options for ${block.label}.`;
    }

    return null;
  }

  if (block.type === "linearScale" || block.type === "rating") {
    const value = getNumberValue(values, block.fieldKey, 0);

    if (block.required && value === 0) {
      return block.validation?.errorText ?? `${block.label} is required.`;
    }

    if (value !== 0 && typeof block.validation?.min === "number" && value < block.validation.min) {
      return block.validation.errorText ?? `${block.label} is below the minimum allowed value.`;
    }

    if (value !== 0 && typeof block.validation?.max === "number" && value > block.validation.max) {
      return block.validation.errorText ?? `${block.label} is above the maximum allowed value.`;
    }

    return null;
  }

  if (block.type === "gridSingle") {
    const value = getGridSingleValue(values, block.fieldKey);
    const missingRow = block.rows.find((row) => !value[row.id]);
    return block.required || block.validation?.requiredRows
      ? missingRow
        ? block.validation?.errorText ?? `Respond to each row in ${block.label}.`
        : null
      : null;
  }

  const value = getGridMultiValue(values, block.fieldKey);
  const missingRow = block.rows.find((row) => (value[row.id] ?? []).length === 0);
  return block.required || block.validation?.requiredRows
    ? missingRow
      ? block.validation?.errorText ?? `Respond to each row in ${block.label}.`
      : null
    : null;
}

export function validateSection(
  section: AgreementFormSection,
  values: AgreementFormValues
): Record<string, string> {
  return Object.fromEntries(
    section.blocks
      .map((block) => [block.id, getBlockValidationError(block, values)] as const)
      .filter((entry): entry is [string, string] => Boolean(entry[1]))
  );
}

export function validateSchema(schema: AgreementFormSchema, values: AgreementFormValues) {
  const sectionErrors = Object.fromEntries(
    schema.sections
      .map((section) => [section.id, validateSection(section, values)] as const)
      .filter((entry) => Object.keys(entry[1]).length > 0)
  );

  const fieldBlockLookup = new Map<
    string,
    {
      blockId: string;
      sectionId: string;
    }
  >();
  let signatureBlockLocation:
    | {
        blockId: string;
        sectionId: string;
      }
    | undefined;

  for (const section of schema.sections) {
    for (const block of section.blocks) {
      if (block.type === "signature") {
        signatureBlockLocation = {
          blockId: block.id,
          sectionId: section.id
        };
        continue;
      }

      if (isFieldBlock(block)) {
        fieldBlockLookup.set(block.fieldKey, {
          blockId: block.id,
          sectionId: section.id
        });
      }
    }
  }

  const today = getTodayDateInputValue();
  const startDate = getStringValue(values, CORE_FORM_FIELD_KEYS.startDate).trim();
  const endDate = getStringValue(values, CORE_FORM_FIELD_KEYS.endDate).trim();
  const signedAt = getStringValue(values, CORE_FORM_FIELD_KEYS.signedAt).trim();

  const assignError = (location: { blockId: string; sectionId: string } | undefined, message: string) => {
    if (!location) {
      return;
    }

    sectionErrors[location.sectionId] = {
      ...(sectionErrors[location.sectionId] ?? {}),
      [location.blockId]: message
    };
  };

  if (endDate) {
    if (startDate) {
      if (endDate < startDate) {
        assignError(fieldBlockLookup.get(CORE_FORM_FIELD_KEYS.endDate), "Internship end date must be on or after the start date.");
      }
    }
  }

  if (signedAt && signedAt !== today) {
    assignError(signatureBlockLocation, "Signing date must be today's date.");
  }

  return sectionErrors;
}

export function getNextSectionId(
  schema: AgreementFormSchema,
  currentSectionId: string,
  values: AgreementFormValues
) {
  const currentIndex = schema.sections.findIndex((section) => section.id === currentSectionId);
  const currentSection = currentIndex >= 0 ? schema.sections[currentIndex] : undefined;

  if (!currentSection) {
    return undefined;
  }

  for (const block of currentSection.blocks) {
    if (block.type !== "multipleChoice" && block.type !== "dropdown") {
      continue;
    }

    const selectedValue = getStringValue(values, block.fieldKey);
    const target = block.options.find((option) => option.value === selectedValue)?.goToSectionId;

    if (target && schema.sections.some((section) => section.id === target)) {
      return target;
    }
  }

  return schema.sections[currentIndex + 1]?.id;
}

export function buildAgreementFormDefaults(input: {
  schema: AgreementFormSchema;
  source: AgreementFormValues;
  templateDefaults: {
    companyAddress?: string;
    confidentialityPeriod?: string;
    jurisdictionCity?: string;
    workingHours?: string;
  };
  user: {
    city?: string;
    fullName: string;
  };
}) {
  const nextValues: AgreementFormValues = {
    ...input.source,
    [CORE_FORM_FIELD_KEYS.agreementDate]:
      getStringValue(input.source, CORE_FORM_FIELD_KEYS.agreementDate) ||
      getTodayDateInputValue(),
    [CORE_FORM_FIELD_KEYS.cityState]: DEFAULT_AGREEMENT_LOCATION,
    [CORE_FORM_FIELD_KEYS.companyAddress]:
      input.templateDefaults.companyAddress ||
      "",
    [CORE_FORM_FIELD_KEYS.confidentialityPeriod]:
      getStringValue(input.source, CORE_FORM_FIELD_KEYS.confidentialityPeriod) ||
      input.templateDefaults.confidentialityPeriod ||
      "",
    [CORE_FORM_FIELD_KEYS.jurisdictionCity]:
      input.templateDefaults.jurisdictionCity ||
      DEFAULT_SIGNATURE_LOCATION,
    [CORE_FORM_FIELD_KEYS.internshipType]: DEFAULT_INTERNSHIP_TYPE,
    [CORE_FORM_FIELD_KEYS.workingHours]:
      input.templateDefaults.workingHours ||
      getStringValue(input.source, CORE_FORM_FIELD_KEYS.workingHours) ||
      "",
    [CORE_FORM_FIELD_KEYS.signatureName]:
      getStringValue(input.source, CORE_FORM_FIELD_KEYS.signatureName) || input.user.fullName,
    [CORE_FORM_FIELD_KEYS.signatureLocation]: DEFAULT_SIGNATURE_LOCATION,
    [CORE_FORM_FIELD_KEYS.signedAt]:
      getStringValue(input.source, CORE_FORM_FIELD_KEYS.signedAt) || getTodayDateInputValue(),
    [CORE_FORM_FIELD_KEYS.signatureDataUrl]:
      getStringValue(input.source, CORE_FORM_FIELD_KEYS.signatureDataUrl),
    [CORE_FORM_FIELD_KEYS.acceptedTerms]:
      getBooleanValue(input.source, CORE_FORM_FIELD_KEYS.acceptedTerms),
    [CORE_FORM_FIELD_KEYS.acceptedConfidentiality]:
      getBooleanValue(input.source, CORE_FORM_FIELD_KEYS.acceptedConfidentiality),
    [CORE_FORM_FIELD_KEYS.acceptedElectronicSignature]:
      getBooleanValue(input.source, CORE_FORM_FIELD_KEYS.acceptedElectronicSignature)
  };

  for (const section of input.schema.sections) {
    for (const block of section.blocks) {
      if (!isFieldBlock(block) || nextValues[block.fieldKey] !== undefined) {
        continue;
      }

      if (block.type === "checkboxes") {
        nextValues[block.fieldKey] = block.defaultValue ?? [];
        continue;
      }

      if (block.type === "linearScale" || block.type === "rating") {
        nextValues[block.fieldKey] = block.defaultValue ?? 0;
        continue;
      }

      if (block.type === "gridSingle") {
        nextValues[block.fieldKey] = {};
        continue;
      }

      if (block.type === "gridMulti") {
        nextValues[block.fieldKey] = {};
        continue;
      }

      nextValues[block.fieldKey] = "defaultValue" in block ? (block.defaultValue ?? "") : "";
    }
  }

  return nextValues;
}
